import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Property, QuoteResponse } from '../types';
import './BookingPage.css';

// 物件の highlights（DBのJSON文字列）を配列にパース
function parseHighlights(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

// ------------------------------------------------------------
// 日付ユーティリティ（ローカルタイムで YYYY-MM-DD を扱う）
// ------------------------------------------------------------
function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function parseISO(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function nightsBetween(checkin: string, checkout: string): number {
  return Math.round(
    (parseISO(checkout).getTime() - parseISO(checkin).getTime()) / 86400000
  );
}

interface DayCell {
  date: Date | null; // null = 前月の空白マス
  iso: string;
}

function buildMonthGrid(year: number, month: number): DayCell[] {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: DayCell[] = [];
  for (let i = 0; i < startWeekday; i++) cells.push({ date: null, iso: '' });
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    cells.push({ date, iso: toISO(date) });
  }
  return cells;
}

function BookingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 曜日ヘッダー（言語切替対応: i18n から取得）
  const WEEKDAYS = t('booking.weekdays', { returnObjects: true }) as string[];

  // カレンダー表示月
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  // 空室情報（ブロック日）
  const [blockedDates, setBlockedDates] = useState<Set<string>>(new Set());
  const [availLoading, setAvailLoading] = useState(false);

  // 選択状態
  const [checkin, setCheckin] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<string | null>(null);
  const [guestCount, setGuestCount] = useState(1);

  // 見積り
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const selectedProperty = properties.find((p) => p.id === selectedPropertyId);

  // ---------- 物件一覧を取得 ----------
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/properties');
        if (!res.ok) throw new Error('Failed to fetch properties');
        const data: Property[] = await res.json();
        setProperties(data);
        if (data.length > 0) setSelectedPropertyId(data[0].id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ---------- 物件・表示月が変わったら空室情報を取得 ----------
  useEffect(() => {
    if (!selectedPropertyId) return;
    const start = toISO(new Date(viewYear, viewMonth, 1));
    // 翌月末までまとめて取得（月またぎの選択に対応）
    const end = toISO(new Date(viewYear, viewMonth + 2, 0));
    setAvailLoading(true);
    fetch(`/api/availability/${selectedPropertyId}?start=${start}&end=${end}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data: { blocked_dates: string[] }) => {
        setBlockedDates(new Set(data.blocked_dates || []));
      })
      .catch(() => setBlockedDates(new Set()))
      .finally(() => setAvailLoading(false));
  }, [selectedPropertyId, viewYear, viewMonth]);

  // ---------- 物件を変えたら選択をリセット ----------
  useEffect(() => {
    setCheckin(null);
    setCheckout(null);
    setGuestCount(1);
    setQuote(null);
    setQuoteError(null);
  }, [selectedPropertyId]);

  // ---------- 見積りを取得（チェックイン/アウト/人数が揃ったら） ----------
  useEffect(() => {
    if (!selectedPropertyId || !checkin || !checkout) {
      setQuote(null);
      setQuoteError(null);
      return;
    }
    setQuoteLoading(true);
    setQuoteError(null);
    fetch('/api/reservations/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        property_id: selectedPropertyId,
        checkin_date: checkin,
        checkout_date: checkout,
        guest_count: guestCount,
      }),
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || 'Quote failed');
        return data as QuoteResponse;
      })
      .then((data) => setQuote(data))
      .catch((err) => {
        setQuote(null);
        setQuoteError(err instanceof Error ? err.message : 'Quote failed');
      })
      .finally(() => setQuoteLoading(false));
  }, [selectedPropertyId, checkin, checkout, guestCount]);

  // ---------- 日付が過去か ----------
  const isPast = (iso: string) => parseISO(iso) < today;

  // ---------- 範囲内にブロック日が含まれるか（checkin<= d < checkout） ----------
  const rangeHasBlocked = (from: string, to: string): boolean => {
    let cur = parseISO(from);
    const end = parseISO(to);
    while (cur < end) {
      if (blockedDates.has(toISO(cur))) return true;
      cur = addDays(cur, 1);
    }
    return false;
  };

  // ---------- チェックアウト候補か（ブロック日でも、宿泊期間中に空きがなければ選択可） ----------
  const isCheckoutCandidate = (iso: string): boolean =>
    !!checkin &&
    !checkout &&
    parseISO(iso) > parseISO(checkin) &&
    !rangeHasBlocked(checkin, iso);

  // ---------- カレンダーのクリック処理 ----------
  const handleDayClick = (iso: string) => {
    if (isPast(iso)) return;
    if (blockedDates.has(iso)) {
      // ブロック日はチェックアウト日としてのみ選択可能
      if (isCheckoutCandidate(iso)) setCheckout(iso);
      return;
    }

    // まだチェックイン未選択、または既に範囲確定済み → 新たにチェックインを設定
    if (!checkin || (checkin && checkout)) {
      setCheckin(iso);
      setCheckout(null);
      return;
    }

    // チェックインは選択済み・チェックアウト未選択
    if (parseISO(iso) <= parseISO(checkin)) {
      // チェックイン以前をクリック → チェックインを付け替え
      setCheckin(iso);
      setCheckout(null);
      return;
    }

    // 途中にブロック日があれば範囲選択不可 → チェックインを付け替え
    if (rangeHasBlocked(checkin, iso)) {
      setCheckin(iso);
      setCheckout(null);
      return;
    }

    setCheckout(iso);
  };

  // ---------- セルの状態判定 ----------
  const cellState = (iso: string) => {
    const past = isPast(iso);
    const blocked = blockedDates.has(iso);
    const isCheckin = checkin === iso;
    const isCheckout = checkout === iso;
    const inRange =
      checkin && checkout &&
      parseISO(iso) > parseISO(checkin) &&
      parseISO(iso) < parseISO(checkout);
    return { past, blocked, isCheckin, isCheckout, inRange };
  };

  const grid = useMemo(
    () => buildMonthGrid(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  const goPrevMonth = () => {
    const d = new Date(viewYear, viewMonth - 1, 1);
    // 過去月には戻らない（当月まで）
    if (d < new Date(today.getFullYear(), today.getMonth(), 1)) return;
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };
  const goNextMonth = () => {
    const d = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const canGoPrev =
    new Date(viewYear, viewMonth, 1) >
    new Date(today.getFullYear(), today.getMonth(), 1);

  const nights = checkin && checkout ? nightsBetween(checkin, checkout) : 0;
  const minNights = selectedProperty?.min_nights ?? 1;
  const maxGuests = selectedProperty?.max_guests ?? 10;

  const fmtMoney = (n: number, currency = 'JPY') =>
    new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2,
    }).format(n);

  if (loading) return <div className="loading">{t('common.loading')}</div>;
  if (error) return <div className="error">{t('common.error')}: {error}</div>;

  return (
    <div className="booking-page">
      <div className="container booking-container">
        <h1 className="booking-title">{t('booking.title')}</h1>

        {/* ① 物件選択 */}
        <section className="booking-section">
          <h2 className="section-heading">
            <span className="step-num">1</span>
            {t('booking.selectProperty')}
          </h2>
          <div className="property-select-list">
            {properties.map((p) => (
              <button
                key={p.id}
                className={`property-pill ${
                  p.id === selectedPropertyId ? 'active' : ''
                }`}
                onClick={() => setSelectedPropertyId(p.id)}
              >
                <span className="pill-name">{p.name}</span>
                <span className="pill-meta">
                  {t('property.maxGuests')}: {p.max_guests}
                </span>
              </button>
            ))}
          </div>

          {selectedProperty && (
            <div className="property-detail-panel">
              {parseHighlights(selectedProperty.highlights).length > 0 && (
                <ul className="pd-highlights">
                  {parseHighlights(selectedProperty.highlights).map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              )}
              <div className="pd-specs">
                {selectedProperty.area_sqm ? <div className="pd-spec"><span>{t('property.spec.area')}</span><strong>{selectedProperty.area_sqm}{t('property.spec.areaUnit')}</strong></div> : null}
                {selectedProperty.bedroom_count ? <div className="pd-spec"><span>{t('property.spec.bedroom')}</span><strong>{selectedProperty.bedroom_count}</strong></div> : null}
                {selectedProperty.living_room_count ? <div className="pd-spec"><span>{t('property.spec.livingRoom')}</span><strong>{selectedProperty.living_room_count}</strong></div> : null}
                {selectedProperty.floor_count ? <div className="pd-spec"><span>{t('property.spec.floors')}</span><strong>{selectedProperty.floor_count}</strong></div> : null}
                {selectedProperty.double_bed_count ? <div className="pd-spec"><span>{t('property.spec.doubleBed')}</span><strong>{selectedProperty.double_bed_count}</strong></div> : null}
                {selectedProperty.single_bed_count ? <div className="pd-spec"><span>{t('property.spec.singleBed')}</span><strong>{selectedProperty.single_bed_count}</strong></div> : null}
                {selectedProperty.futon_count ? <div className="pd-spec"><span>{t('property.spec.futon')}</span><strong>{selectedProperty.futon_count}</strong></div> : null}
                {selectedProperty.double_sofabed_count ? <div className="pd-spec"><span>{t('property.spec.doubleSofabed')}</span><strong>{selectedProperty.double_sofabed_count}</strong></div> : null}
                {selectedProperty.single_sofabed_count ? <div className="pd-spec"><span>{t('property.spec.singleSofabed')}</span><strong>{selectedProperty.single_sofabed_count}</strong></div> : null}
                {selectedProperty.toilet_count ? <div className="pd-spec"><span>{t('property.spec.toilet')}</span><strong>{selectedProperty.toilet_count}</strong></div> : null}
                {selectedProperty.bath_count ? <div className="pd-spec"><span>{t('property.spec.bath')}</span><strong>{selectedProperty.bath_count}</strong></div> : null}
              </div>
            </div>
          )}
        </section>

        {/* ② カレンダー */}
        <section className="booking-section">
          <h2 className="section-heading">
            <span className="step-num">2</span>
            {t('booking.selectDates')}
          </h2>

          <div className="calendar">
            <div className="calendar-header">
              <button
                className="cal-nav"
                onClick={goPrevMonth}
                disabled={!canGoPrev}
                aria-label={t('booking.prevMonth')}
              >
                ‹
              </button>
              <span className="cal-month">
                {t('booking.calMonthYear', { year: viewYear, month: viewMonth + 1 })}
              </span>
              <button className="cal-nav" onClick={goNextMonth} aria-label={t('booking.nextMonth')}>
                ›
              </button>
            </div>

            <div className="calendar-weekdays">
              {WEEKDAYS.map((w, i) => (
                <div
                  key={w}
                  className={`weekday ${i === 0 ? 'sun' : ''} ${
                    i === 6 ? 'sat' : ''
                  }`}
                >
                  {w}
                </div>
              ))}
            </div>

            <div className="calendar-grid">
              {grid.map((cell, idx) => {
                if (!cell.date) {
                  return <div key={`empty-${idx}`} className="day empty" />;
                }
                const { past, blocked, isCheckin, isCheckout, inRange } =
                  cellState(cell.iso);
                const checkoutOnly = blocked && !past && isCheckoutCandidate(cell.iso);
                const classes = ['day'];
                if (past) classes.push('past');
                if (blocked) classes.push('blocked');
                if (checkoutOnly) classes.push('checkout-only');
                if (isCheckin) classes.push('checkin');
                if (isCheckout) classes.push('checkout');
                if (inRange) classes.push('in-range');
                if (!past && !blocked) classes.push('available');
                return (
                  <button
                    key={cell.iso}
                    className={classes.join(' ')}
                    disabled={past || (blocked && !checkoutOnly && !isCheckout)}
                    onClick={() => handleDayClick(cell.iso)}
                    title={
                      checkoutOnly
                        ? t('booking.checkoutOnly')
                        : blocked
                        ? t('property.unavailable')
                        : past
                        ? ''
                        : t('property.available')
                    }
                  >
                    <span className="day-num">{cell.date.getDate()}</span>
                    {blocked && <span className="day-mark">×</span>}
                  </button>
                );
              })}
            </div>

            {availLoading && (
              <div className="cal-loading">{t('common.loading')}</div>
            )}

            <div className="calendar-legend">
              <span className="legend-item">
                <i className="swatch avail" /> {t('property.available')}
              </span>
              <span className="legend-item">
                <i className="swatch blocked" /> {t('property.unavailable')}
              </span>
              <span className="legend-item">
                <i className="swatch selected" /> {t('booking.selectDates')}
              </span>
            </div>
          </div>

          {/* 選択サマリ */}
          <div className="date-summary">
            <div className="date-box">
              <span className="date-label">{t('property.checkinTime')}</span>
              <span className="date-value">{checkin || '—'}</span>
            </div>
            <span className="date-arrow">→</span>
            <div className="date-box">
              <span className="date-label">{t('property.checkoutTime')}</span>
              <span className="date-value">{checkout || '—'}</span>
            </div>
            {nights > 0 && (
              <div className="nights-badge">{t('booking.nightsCount', { count: nights })}</div>
            )}
          </div>
          {checkin && checkout && nights < minNights && (
            <p className="inline-warn">
              {t('property.minNights')}: {minNights}
            </p>
          )}
        </section>

        {/* ③ 人数 */}
        <section className="booking-section">
          <h2 className="section-heading">
            <span className="step-num">3</span>
            {t('booking.selectGuests')}
          </h2>
          <div className="guest-stepper">
            <button
              className="step-btn"
              onClick={() => setGuestCount((g) => Math.max(1, g - 1))}
              disabled={guestCount <= 1}
              aria-label={t('booking.decrease')}
            >
              −
            </button>
            <span className="guest-count">{guestCount}</span>
            <button
              className="step-btn"
              onClick={() => setGuestCount((g) => Math.min(maxGuests, g + 1))}
              disabled={guestCount >= maxGuests}
              aria-label={t('booking.increase')}
            >
              +
            </button>
            <span className="guest-hint">
              {t('property.maxGuests')}: {maxGuests}
            </span>
          </div>
        </section>

        {/* ④ 見積り */}
        <section className="booking-section quote-section">
          <h2 className="section-heading">
            <span className="step-num">4</span>
            {t('booking.priceBreakdown')}
          </h2>

          {!checkin || !checkout ? (
            <p className="quote-empty">{t('booking.selectDates')}</p>
          ) : quoteLoading ? (
            <p className="quote-empty">{t('booking.processing')}</p>
          ) : quoteError ? (
            <p className="error-inline">{quoteError}</p>
          ) : quote ? (
            <div className="quote-box">
              <div className="quote-row">
                <span>{t('booking.baseAmount')}（{t('booking.nightsCount', { count: quote.nights })}）</span>
                <span>{fmtMoney(quote.base_amount, quote.currency)}</span>
              </div>
              {quote.extra_guest_amount > 0 && (
                <div className="quote-row">
                  <span>{t('booking.extraGuestAmount')}</span>
                  <span>{fmtMoney(quote.extra_guest_amount, quote.currency)}</span>
                </div>
              )}
              <div className="quote-row">
                <span>{t('booking.cleaningAmount')}</span>
                <span>{fmtMoney(quote.cleaning_amount, quote.currency)}</span>
              </div>
              <div className="quote-row">
                <span>{t('booking.taxAmount')}</span>
                <span>{fmtMoney(quote.tax_amount, quote.currency)}</span>
              </div>
              {quote.discount_amount > 0 && (
                <div className="quote-row discount">
                  <span>{t('booking.discountAmount')}</span>
                  <span>-{fmtMoney(quote.discount_amount, quote.currency)}</span>
                </div>
              )}
              <div className="quote-row total">
                <span>{t('booking.total')}</span>
                <span>{fmtMoney(quote.total_price, quote.currency)}</span>
              </div>
            </div>
          ) : null}

          <button
            className="btn btn-book"
            disabled={!quote || quoteLoading || !!quoteError}
            onClick={() => {
              // ゲスト情報入力ページへ選択内容と見積りを引き渡す
              navigate('/booking/guest', {
                state: {
                  property: selectedProperty,
                  checkin,
                  checkout,
                  guestCount,
                  quote,
                },
              });
            }}
          >
            {t('booking.confirmBooking')}
          </button>
        </section>
      </div>
    </div>
  );
}

export default BookingPage;
